// Client-side mirror of backend feature_catalog.compute_quote (server quote is the source of truth at checkout)
const r2 = (n) => Math.round(n * 100) / 100;

export function money(n, { compact = false } = {}) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (compact && Number.isInteger(a)) return `${sign}£${a.toLocaleString("en-GB")}`;
  return `${sign}£${a.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function clampSeats(config, seats) {
  const n = parseInt(seats, 10);
  const min = config?.min_seats ?? 1;
  const max = config?.max_seats ?? 500;
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

export function presetFor(catalog, features) {
  const set = new Set(features || []);
  return (catalog?.presets || []).find((p) => p.feature_keys.length === set.size && p.feature_keys.every((k) => set.has(k))) || null;
}

export function computeQuote(catalog, { seats, features, cycle }, bundleDiscountPct, resellerDiscountPct = 0) {
  const config = catalog?.config || { per_user_monthly: 0, annual_discount_pct: 0, min_seats: 1, max_seats: 500 };
  const priceOf = Object.fromEntries((catalog?.addons || []).map((a) => [a.key, a]));
  const sellable = (features || []).filter((k) => priceOf[k] && priceOf[k].sellable !== false);
  const n = clampSeats(config, seats);
  const seatTotal = r2(config.per_user_monthly * n);
  const addonLines = sellable.map((k) => ({ key: k, label: priceOf[k].label, monthly_price: priceOf[k].monthly_price }));
  const addonsTotal = r2(addonLines.reduce((s, a) => s + a.monthly_price, 0));
  const listMonthly = r2(seatTotal + addonsTotal);
  const preset = bundleDiscountPct == null ? presetFor(catalog, sellable) : null;
  const bundlePct = bundleDiscountPct != null ? bundleDiscountPct : preset?.bundle_discount_pct || 0;
  const monthlyBundled = r2(listMonthly * (1 - bundlePct / 100));
  const resellerPct = Math.max(0, Math.min(Number(resellerDiscountPct) || 0, 100));
  const monthlyTotal = r2(monthlyBundled * (1 - resellerPct / 100));
  const annualTotal = r2(monthlyTotal * 12 * (1 - config.annual_discount_pct / 100));
  const c = cycle === "annual" ? "annual" : "monthly";
  return {
    seats: n, features: sellable, cycle: c,
    per_user_monthly: config.per_user_monthly, seat_total: seatTotal,
    addons: addonLines, addons_total: addonsTotal,
    list_monthly: listMonthly, bundle_discount_pct: bundlePct, bundle_saving: r2(listMonthly - monthlyBundled),
    reseller_discount_pct: resellerPct, reseller_saving: r2(monthlyBundled - monthlyTotal),
    monthly_total: monthlyTotal,
    annual_discount_pct: config.annual_discount_pct, annual_total: annualTotal,
    annual_monthly_equivalent: r2(annualTotal / 12), annual_saving: r2(monthlyTotal * 12 - annualTotal),
    per_cycle_total: c === "annual" ? annualTotal : monthlyTotal,
    monthly_equivalent: c === "annual" ? r2(annualTotal / 12) : monthlyTotal,
    preset,
  };
}

export function selectionToQuery(sel) {
  const q = new URLSearchParams();
  q.set("preset", sel.presetId || "custom");
  q.set("seats", String(sel.seats));
  q.set("features", (sel.features || []).join(","));
  q.set("cycle", sel.cycle || "monthly");
  return q.toString();
}

export function selectionFromQuery(search) {
  const q = new URLSearchParams(search);
  if (!q.get("preset") && !q.get("seats") && !q.get("features")) return null;
  return {
    presetId: q.get("preset") || "custom",
    seats: q.get("seats") ? parseInt(q.get("seats"), 10) : null,
    features: q.get("features") != null ? q.get("features").split(",").filter(Boolean) : null,
    cycle: q.get("cycle") === "annual" ? "annual" : "monthly",
  };
}
